import { EdgelessLegacySlotIdentifier } from '@blocksuite/affine-block-surface';
import { WithDisposable } from '@blocksuite/global/lit';
import { type EditorHost, ShadowlessElement } from '@blocksuite/std';
import { baseTheme } from '@streamlain/theme';
import { css, html, type PropertyValues, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';

import {
  DEFAULT_FRAME_PANEL_HEADER_LABELS,
  type FramePanelHeaderLabels,
} from './header/frame-panel-header.js';

const styles = css`
  affine-frame-panel {
    flex-grow: 1;
  }

  frame-panel {
    display: block;
    width: 100%;
    height: 100%;
  }

  .frame-panel-container {
    background-color: var(--streamlain-v2-layer-background-primary);
    box-sizing: border-box;

    display: flex;
    flex-direction: column;
    align-items: stretch;

    height: 100%;
    font-family: ${unsafeCSS(baseTheme.fontSansFamily)};
  }

  .frame-panel-body {
    padding: 12px 8px 8px;
    flex-grow: 1;
    width: 100%;

    overflow: auto;
    overflow-x: hidden;
    scrollbar-width: thin; /* For Firefox */
    scrollbar-color: transparent transparent; /* For Firefox */
  }

  .frame-panel-body::-webkit-scrollbar {
    width: 4px;
  }

  .frame-panel-body::-webkit-scrollbar-thumb {
    border-radius: 2px;
  }

  .frame-panel-body:hover::-webkit-scrollbar-thumb {
    background-color: var(--streamlain-v2-layer-blackAlpha-30);
  }

  .frame-panel-body::-webkit-scrollbar-track {
    background-color: transparent;
  }

  .frame-panel-body::-webkit-scrollbar-corner {
    display: none;
  }
`;

export const AFFINE_FRAME_PANEL = 'affine-frame-panel';

export class FramePanel extends WithDisposable(ShadowlessElement) {
  static override styles = styles;

  private _hasNotifiedOpen = false;

  private _notifyFramePanelState(open: boolean) {
    const slots = this.host?.std?.getOptional(EdgelessLegacySlotIdentifier);
    if (slots) {
      slots.navigatorSettingUpdated.next({ framePanelOpen: open });
      return true;
    }
    return false;
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    // Notify that frame panel is closed
    this._notifyFramePanelState(false);
    this._hasNotifiedOpen = false;
  }

  override firstUpdated() {
    // Notify on first render if host is already available
    if (
      this.host &&
      !this._hasNotifiedOpen &&
      this._notifyFramePanelState(true)
    ) {
      this._hasNotifiedOpen = true;
    }
  }

  override updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    // Notify when host becomes available (property is set after mount)
    if (
      changedProperties.has('host') &&
      this.host &&
      !this._hasNotifiedOpen &&
      this._notifyFramePanelState(true)
    ) {
      this._hasNotifiedOpen = true;
    }
  }

  override render() {
    return html`<div class="frame-panel-container">
      <affine-frame-panel-header
        .editorHost=${this.host}
        .labels=${this.labels}
      ></affine-frame-panel-header>
      <affine-frame-panel-body
        class="frame-panel-body"
        .editorHost=${this.host}
        .fitPadding=${this.fitPadding}
      ></affine-frame-panel-body>
    </div>`;
  }

  @property({ attribute: false })
  accessor fitPadding: number[] = [50, 380, 50, 50];

  @property({ attribute: false })
  accessor host!: EditorHost;

  @property({ attribute: false })
  accessor labels: FramePanelHeaderLabels = DEFAULT_FRAME_PANEL_HEADER_LABELS;
}

declare global {
  interface HTMLElementTagNameMap {
    [AFFINE_FRAME_PANEL]: FramePanel;
  }
}
